// components/data-quality/TerminalQualityComparison.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingColumns } from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TerminalQualityComparison = ({ terminalStats }) => {
  // Limit to top 10 terminals by booking count
  const topTerminals = [...terminalStats]
    .sort((a, b) => b.totalBookings - a.totalBookings)
    .slice(0, 10);

  // Data for horizontal bar chart
  const chartData = {
    labels: topTerminals.map((item) => item.terminal),
    datasets: [
      {
        label: "Quality Score",
        data: topTerminals.map((item) => item.averageQualityScore),
        backgroundColor: topTerminals.map((item) => {
          const score = item.averageQualityScore;
          if (score >= 90) return "rgba(34, 197, 94, 0.8)"; // Green
          if (score >= 70) return "rgba(59, 130, 246, 0.8)"; // Blue
          if (score >= 50) return "rgba(234, 179, 8, 0.8)"; // Yellow
          if (score >= 30) return "rgba(249, 115, 22, 0.8)"; // Orange
          return "rgba(239, 68, 68, 0.8)"; // Red
        }),
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Abandoned %",
        data: topTerminals.map((item) => item.abandonedPercentage),
        backgroundColor: "rgba(239, 68, 68, 0.6)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  // Options for horizontal bar chart
  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.parsed.x.toFixed(1)}%`;
          },
          afterLabel: (context) => {
            const terminal = topTerminals[context.dataIndex];
            return [
              `Bookings: ${terminal.totalBookings}`,
              `Valid Status: ${terminal.validStatusPercentage.toFixed(1)}%`,
              `Valid Dates: ${terminal.validDatePercentage.toFixed(1)}%`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Percentage (%)",
          font: {
            size: 11,
          },
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      y: {
        ticks: {
          callback: (value) => {
            const terminal = topTerminals[value];
            if (!terminal) return value;

            const shortName =
              terminal.terminal.length > 25
                ? terminal.terminal.substring(0, 22) + "..."
                : terminal.terminal;

            return `${shortName} (${terminal.totalBookings})`;
          },
          font: {
            size: 10,
          },
        },
      },
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon
            icon={faBuildingColumns}
            className="text-blue-600 mr-2"
          />
          Terminal Quality Comparison
        </h3>
        <div className="text-sm text-gray-500">
          Top {topTerminals.length} terminals
        </div>
      </div>

      <div className="flex-grow">
        <Bar data={chartData} options={chartOptions} />
      </div>

      <div className="mt-2 text-xs text-gray-500">
        <p>
          Terminal comparison shows average quality score and abandoned
          percentage across different terminals.
        </p>
      </div>
    </div>
  );
};

export default TerminalQualityComparison;
